import { getOrganization } from 'api/generated/api/organization';
import { referenceDataSwrOptions } from 'api/swrConfig';
import type { OrgPhoneNumbersResponse } from 'api/generated/types/orgPhoneNumbersResponse';
import type { OrgPhoneNumberItem } from 'api/generated/types/orgPhoneNumberItem';

import { useApiSWR } from './useApiSWR';
import { orgPhoneNumbersKey } from './orgKeys';

const { organizationGetOrgPhoneNumbers } = getOrganization();
const EMPTY_PHONE_NUMBERS: OrgPhoneNumberItem[] = [];

const fetcher = async (orgId: string): Promise<OrgPhoneNumbersResponse> => {
  return organizationGetOrgPhoneNumbers(orgId);
};

export const useOrgPhoneNumbers = (orgId: string | null) => {
  const { data, error, isLoading } = useApiSWR(
    orgPhoneNumbersKey(orgId),
    ([, id]) => fetcher(id),
    {
      ...referenceDataSwrOptions,
      // The banner lives in the persistent app Layout and never remounts, so
      // focus/reconnect also picks up changes made in another session. Local
      // purchases and releases explicitly revalidate the shared key. The preset's
      // deduping window keeps passive refetches sane.
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    phoneNumbers: data?.phone_numbers ?? EMPTY_PHONE_NUMBERS,
    isLoading,
    hasError: Boolean(error),
    // The SWR error itself, stable across cached replays, so a caller can
    // report each failure once.
    error,
    // Only warning-worthy once the fetch succeeds, so we never flash during
    // load or on a transient error.
    hasNoPhoneNumbers: !isLoading && !error && data?.total === 0,
  };
};
